import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ButtonTheme, ButtonType, NotificationTheme, LoaderTheme } from '@fcom/ui-components';
import { FormStatus, TypedFormGroup } from '@fcom/service-forms/interfaces';
import { enableConditionalFields, disableConditionalFields, trackFormState } from '@fcom/service-forms/services/utils';
import { GtmService } from '@fcom/common/gtm';
import { Profile, LoginAddress, LoginAddressType, ProfileTier } from '@fcom/core-api/login';
import { unsubscribe, LATIN_ALPHA_PATTERN, LATIN_ADDRESS_PATTERN, trimAndReplaceWhitespace } from '@fcom/core/utils';
import { SelectOption } from '@fcom/common';
import { LanguageService } from '@fcom/ui-translate';
import { finShare } from '@fcom/rx';
import { CountryCode } from '@fcom/core';

import { MailingAddressFormField, MailingAddressSubmissionData } from '../../interfaces/profile.interface';
import { ProfileService } from '../../services/profile.service';

const MAILING_ADDRESS_FORM = 'update-profile-mailing-address';

@Component({
  selector: 'fin-loyalty-mailing-address-form',
  templateUrl: 'loyalty-mailing-address-form.component.html',
  styleUrls: ['./loyalty-mailing-address-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoyaltyMailingAddressFormComponent implements OnInit, OnDestroy {
  public readonly FormStatus = FormStatus;
  public readonly ButtonTheme = ButtonTheme;
  public readonly ButtonType = ButtonType;
  public readonly IconLibrary = IconLibrary;
  public readonly LoginAddressType = LoginAddressType;
  public readonly MailingAddressFormField = MailingAddressFormField;
  public readonly NotificationTheme = NotificationTheme;
  public readonly LoaderTheme = LoaderTheme;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  public notification: ElementRef;

  @ViewChild('fcomNotification', { static: false })
  set notificationElem(notificationElem: ElementRef) {
    this.notification = notificationElem;
  }

  @Input()
  enableCancel = false;

  @Output()
  cancel: EventEmitter<void> = new EventEmitter();

  @Output()
  success: EventEmitter<LoginAddress> = new EventEmitter();

  reactiveForm: TypedFormGroup;
  countries$: Observable<SelectOption[]>;
  selectedCountryText: object;
  formStatus: FormStatus = FormStatus.INITIAL;
  profile: Profile;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private profileService: ProfileService,
    private languageService: LanguageService,
    private gtmService: GtmService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.formStatus = FormStatus.LOADING;
    const countryCodes$: Observable<CountryCode[]> = this.languageService.translate('countryCodes').pipe(finShare());

    this.countries$ = countryCodes$.pipe(
      map((countryCodes) => {
        const selectOptions: SelectOption[] = countryCodes.map((countryCodeData) => ({
          value: countryCodeData.countryCode,
          name: countryCodeData.name,
        }));
        selectOptions.unshift({ value: '', name: '-' });
        return selectOptions;
      }),
      take(1)
    );

    this.subscriptions.add(
      this.profileService.getProfile().subscribe((profile: Profile) => {
        this.profile = profile;
        this.createForm(profile);
        this.formStatus = FormStatus.INITIAL;
      })
    );
    this.selectedCountryText = {
      AU: {
        translation: 'SERVICE_FORMS.serviceForms.fields.stateTerritory',
        options: this.profileService.getCountryRegionOptions('au'),
      },
      CA: {
        translation: 'SERVICE_FORMS.serviceForms.fields.province',
        options: this.profileService.getCountryRegionOptions('ca'),
      },
      US: {
        translation: 'SERVICE_FORMS.serviceForms.fields.state',
        options: this.profileService.getCountryRegionOptions('us'),
      },
      JP: {
        translation: 'SERVICE_FORMS.serviceForms.fields.prefecture',
      },
      GB: {
        translation: 'SERVICE_FORMS.serviceForms.fields.buildingName',
      },
    };
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  onSubmit(): void {
    if (this.formStatus === FormStatus.LOADING) {
      return;
    }

    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();

      return;
    }

    this.formStatus = FormStatus.LOADING;

    const data: MailingAddressSubmissionData = {
      countryCode: this.reactiveForm.get('workAddress')?.get(MailingAddressFormField.PRIMARY)?.value
        ? this.reactiveForm.get('workAddress').get(MailingAddressFormField.COUNTRY).value
        : this.reactiveForm.get('homeAddress').get(MailingAddressFormField.COUNTRY).value,
      addresses: [
        {
          ...this.reactiveForm.get('homeAddress').value,
          primary: this.reactiveForm.get('type').value === LoginAddressType.HOME,
          type: LoginAddressType.HOME,
        },
      ],
    };

    if (this.reactiveForm.get('workAddress').value) {
      data.addresses.push({
        ...this.reactiveForm.get('workAddress').value,
        primary: this.reactiveForm.get('type').value === LoginAddressType.JOB,
        type: LoginAddressType.JOB,
      });
    }

    trackFormState(MAILING_ADDRESS_FORM, 'send-attempt', this.gtmService);

    this.subscriptions.add(
      this.profileService.updateMailingAddress(data).subscribe({
        next: (result) => {
          if (result.status.message === 'OK') {
            this.formStatus = FormStatus.COMPLETE;
            trackFormState(MAILING_ADDRESS_FORM, 'success', this.gtmService);
            this.success.emit();
          } else {
            this.formStatus = FormStatus.ERROR;
            trackFormState(MAILING_ADDRESS_FORM, 'error', this.gtmService);
          }
        },
        error: () => {
          this.formStatus = FormStatus.ERROR;
          trackFormState(MAILING_ADDRESS_FORM, 'error', this.gtmService);
        },
        complete: () => {
          this.cdRef.detectChanges();
          this.notification.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
      })
    );
  }

  onCountrySelect(country: string): void {
    const form = this.activeForm();
    this.updateConditionalFields(country, form);
    form.get(MailingAddressFormField.STATE_OR_PROVINCE).reset();
  }

  updateConditionalFields(country: string, form: UntypedFormGroup): void {
    switch (country) {
      case 'AU':
      case 'CA':
      case 'US':
        disableConditionalFields([MailingAddressFormField.BUILDING_NAME_OR_PREFECTURE], form);
        enableConditionalFields([MailingAddressFormField.STATE_OR_PROVINCE], form);
        break;
      case 'JP':
      case 'GB':
        disableConditionalFields([MailingAddressFormField.STATE_OR_PROVINCE], form);
        enableConditionalFields([MailingAddressFormField.BUILDING_NAME_OR_PREFECTURE], form);
        break;
      default:
        disableConditionalFields([MailingAddressFormField.STATE_OR_PROVINCE], form);
        disableConditionalFields([MailingAddressFormField.BUILDING_NAME_OR_PREFECTURE], form);
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onValueChange(form: UntypedFormGroup, field: MailingAddressFormField): void {
    form.get(field).setValue(trimAndReplaceWhitespace(form.get(field).value));
  }

  private createForm(profile: Profile) {
    const primaryAddress = profile.addresses.find((address) => address.primary);
    let homeAddress = profile.addresses.find((address) => address.type === LoginAddressType.HOME);
    const jobAddress = profile.addresses.find((address) => address.type === LoginAddressType.JOB);

    if (!homeAddress) {
      homeAddress = {
        [MailingAddressFormField.CITY]: '',
        [MailingAddressFormField.COMPANY_NAME]: '',
        [MailingAddressFormField.COUNTRY]: '',
        [MailingAddressFormField.POSTAL_CODE]: '',
        [MailingAddressFormField.PRIMARY]: jobAddress ? !jobAddress.primary : true,
        [MailingAddressFormField.STATE_OR_PROVINCE]: '',
        [MailingAddressFormField.BUILDING_NAME_OR_PREFECTURE]: '',
        [MailingAddressFormField.STREET_ADDRESS]: '',
        type: LoginAddressType.HOME,
      };
    }

    this.reactiveForm = this.fb.group({
      type: {
        value: primaryAddress.type !== LoginAddressType.NONE ? primaryAddress.type : LoginAddressType.HOME,
        disabled: !jobAddress,
      },
      homeAddress: this.createAddressForm(homeAddress, profile.tier),
      workAddress: jobAddress && this.createAddressForm(jobAddress, profile.tier),
    }) as TypedFormGroup;

    this.updateConditionalFields(homeAddress.country, this.reactiveForm.get('homeAddress') as UntypedFormGroup);
    jobAddress &&
      this.updateConditionalFields(jobAddress.country, this.reactiveForm.get('workAddress') as UntypedFormGroup);
  }

  private createAddressForm(address: LoginAddress, profileTier: ProfileTier): UntypedFormGroup {
    const requiredValidator = profileTier === ProfileTier.ACCOUNT ? [] : [Validators.required];

    return this.fb.group({
      [MailingAddressFormField.COMPANY_NAME]: {
        value: address.companyName,
        disabled: address.type !== LoginAddressType.JOB,
      },
      [MailingAddressFormField.COUNTRY]: [address ? address.country : ''],
      [MailingAddressFormField.STREET_ADDRESS]: [
        address && address.streetAddress !== '-' ? address.streetAddress : null,
        [...requiredValidator, Validators.pattern(LATIN_ADDRESS_PATTERN)],
      ],
      [MailingAddressFormField.POSTAL_CODE]: [
        address ? address.postalCode : null,
        [...requiredValidator, Validators.pattern(LATIN_ADDRESS_PATTERN)],
      ],
      [MailingAddressFormField.CITY]: [
        address && address.city !== '-' ? address.city : null,
        [...requiredValidator, Validators.pattern(LATIN_ALPHA_PATTERN)],
      ],
      [MailingAddressFormField.STATE_OR_PROVINCE]: [{ value: address ? address.stateOrProvince : '', disabled: true }],
      [MailingAddressFormField.BUILDING_NAME_OR_PREFECTURE]: [
        { value: address ? address.buildingNameOrPrefecture : null, disabled: true },
      ],
    });
  }

  private activeForm = () =>
    (this.reactiveForm.get('type').value === LoginAddressType.HOME
      ? this.reactiveForm.get('homeAddress')
      : this.reactiveForm.get('workAddress')) as UntypedFormGroup;
}
